import { AlertTriangle, ExternalLink, Loader2 } from 'lucide-react';
import type { DuplicateMatch } from '../api/client';

interface Props {
  matches: DuplicateMatch[];
  loading: boolean;
  onDismiss: () => void;
}

const reasonLabels: Record<string, string> = {
  email: 'Email match',
  phone: 'Phone match',
  name: 'Similar name',
};

export default function DuplicateContactWarning({ matches, loading, onDismiss }: Props) {
  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.6rem 0.75rem', background: '#fef9e7',
        border: '1px solid #f0d78c', borderRadius: 6, marginTop: '0.75rem',
        fontSize: '0.82rem', color: '#7c6a0a',
      }}>
        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
        Checking for duplicates...
      </div>
    );
  }

  if (matches.length === 0) return null;

  return (
    <div style={{
      padding: '0.75rem', background: '#fef9e7',
      border: '1px solid #f0d78c', borderRadius: 6, marginTop: '0.75rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
        <AlertTriangle size={15} color="#b7930a" />
        <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#7c6a0a' }}>
          Possible duplicate{matches.length > 1 ? 's' : ''} found
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {matches.map((m) => (
          <div
            key={m.contact_id}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.4rem 0.6rem', background: '#fff',
              border: '1px solid #f0e6c0', borderRadius: 4, fontSize: '0.82rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 500 }}>{m.first_name} {m.last_name}</span>
              {m.email && <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{m.email}</span>}
              {m.phone && <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{m.phone}</span>}
              <span style={{
                display: 'inline-block', padding: '0.1rem 0.4rem',
                background: m.match_reason === 'name' ? '#e8e3f3' : '#dff0d8',
                color: m.match_reason === 'name' ? '#5b3e99' : '#3c763d',
                borderRadius: 10, fontSize: '0.7rem', fontWeight: 600,
              }}>
                {reasonLabels[m.match_reason] ?? m.match_reason}
                {m.match_reason === 'name' && ` (${Math.round(m.score * 100)}%)`}
              </span>
            </div>
            <a
              href={`/contacts/${m.contact_id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
                color: 'var(--primary)', fontSize: '0.78rem', textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              View <ExternalLink size={11} />
            </a>
          </div>
        ))}
      </div>

      <button
        onClick={onDismiss}
        style={{
          marginTop: '0.5rem', background: 'none', border: 'none',
          color: '#7c6a0a', fontSize: '0.78rem', cursor: 'pointer',
          textDecoration: 'underline', padding: 0,
        }}
      >
        I've checked — create anyway
      </button>
    </div>
  );
}
